import { Group, Stack, Card, Center, Flex } from "@mantine/core";
import type { ReactNode } from "react";
import { memo, useCallback, type FC } from "react";
import { MapLibreMap } from "@mapka/react-maplibre-map";
import { addTilesetToMap, getExampleStyle } from "../utils/example";
import { TilesetItemControls } from "./TilesetItemControls";
import type { Map } from "@mapka/react-maplibre-map";
import type { Tileset, TilesetDataset, TilesetVersion } from "../types/tileset";
import { StatusIcon } from "./StatusIcon";
import { useDefaultApiKey } from "../../../../hooks/apiKey";
import { useInViewport } from "@mantine/hooks";
import { TilesetItemTitle } from "./TilesetItemTitle";
import { TilesetUsageChart } from "./TilesetUsageChart";
import type { AccountInfo } from "@mapka/style-editor";

interface TilesetItemProps {
  tileset: Tileset;
  account: AccountInfo;
  version: TilesetVersion | null;
  dataset: TilesetDataset | null;
  controls?: ReactNode;
  title?: ReactNode;
  info?: ReactNode;
}

export const TilesetItem: FC<TilesetItemProps> = memo(({ tileset, version, account, dataset, controls, title, info }) => {
  const apiKey = useDefaultApiKey();
  const { ref, inViewport } = useInViewport();

  const onMapLoaded = useCallback((map: Map) => {
    addTilesetToMap(
      map,
      account,
      tileset,
      dataset,
      version,
    );
  }, [tileset, account, version]);

  const mapLibreStyle = getExampleStyle();

  return (
    <Card ref={ref} p="0">
      <Flex h="300px">
        <Stack h="100%" flex={1}>
          {version?.status === "ready" && inViewport && (
            <MapLibreMap
              apiKey={apiKey}
              style={mapLibreStyle}
              onMapLoaded={onMapLoaded}
            />
          )}
          {version?.status !== "ready" && inViewport && (
            <Center h="100%">
              <StatusIcon version={version} />
            </Center>
          )}
        </Stack>

        <Stack h="100%" flex={1} p="sm">
          <Group justify="space-between">
            {title && title}
            {!title && (
              <TilesetItemTitle
                tileset={tileset}
                dataset={dataset}
                version={version}
              />
            )}
            {controls && controls}
            {!controls && (
              <TilesetItemControls
                account={account}
                tileset={tileset}
                version={version}
                dataset={dataset}
              />
            )}
          </Group>
          {info && info}
          {!info && inViewport && (
            <TilesetUsageChart tileset={tileset} />
          )}
        </Stack>
      </Flex>
    </Card>
  );
});

TilesetItem.displayName = "TilesetItemControls";
